import { useState, type JSX } from 'react';
import { resolveProposedAppChange } from '../lib/api';
import type {
  AppChangeResolution,
  ProposedAppChange,
  ScanAnalysisQueueItem,
} from '@obd-reader/shared';
import { theme } from '../theme';

const RESOLUTION_OPTIONS: ReadonlyArray<{ value: AppChangeResolution; label: string }> = [
  { value: 'implemented', label: 'Implemented' },
  { value: 'rejected-misdiagnosis', label: 'Rejected — misdiagnosis' },
  { value: 'rejected-hallucination', label: 'Rejected — hallucination' },
  { value: 'rejected-duplicate', label: 'Rejected — duplicate' },
  { value: 'rejected-other', label: 'Rejected — other (note required)' },
  { value: 'superseded', label: 'Superseded' },
];

export const RESOLUTION_PALETTE: Record<
  AppChangeResolution,
  { bg: string; fg: string; label: string }
> = {
  implemented: { bg: theme.status.success, fg: '#013220', label: 'Implemented' },
  'rejected-misdiagnosis': { bg: theme.status.error, fg: '#3a0000', label: 'Misdiagnosis' },
  'rejected-hallucination': { bg: theme.status.error, fg: '#3a0000', label: 'Hallucination' },
  'rejected-duplicate': {
    bg: theme.surface.borderStrong,
    fg: theme.text.primary,
    label: 'Duplicate',
  },
  'rejected-other': { bg: theme.surface.borderStrong, fg: theme.text.primary, label: 'Rejected' },
  superseded: { bg: theme.status.warning, fg: '#3a2200', label: 'Superseded' },
};

export interface ProposedAppChangeResolutionControlProps {
  scanId: string;
  index: number;
  change: ProposedAppChange;
  onResolved: (item: ScanAnalysisQueueItem) => void;
}

export function ProposedAppChangeResolutionControl(
  props: ProposedAppChangeResolutionControlProps,
): JSX.Element {
  const { scanId, index, change, onResolved } = props;
  const [editing, setEditing] = useState(false);
  const [resolution, setResolution] = useState<AppChangeResolution>(
    change.resolution ?? 'implemented',
  );
  const [note, setNote] = useState(change.resolutionNote ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const noteRequired = resolution === 'rejected-other';
  const trimmedNote = note.trim();
  const submitDisabled = submitting || (noteRequired && !trimmedNote);

  const onSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await resolveProposedAppChange(scanId, index, {
        resolution,
        ...(trimmedNote ? { resolutionNote: trimmedNote } : {}),
      });
      onResolved(res.item);
      setEditing(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const currentBadge = change.resolution ? (
    <ResolutionBadge
      resolution={change.resolution}
      note={change.resolutionNote}
      resolvedByDisplayName={change.resolvedByDisplayName}
      resolvedBy={change.resolvedBy}
      resolvedAt={change.resolvedAt}
    />
  ) : null;

  return (
    <div
      style={{
        marginTop: 6,
        padding: '6px 8px',
        background: theme.surface.panel,
        borderRadius: theme.radius.sm,
        border: `1px solid ${theme.surface.border}`,
        fontSize: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <strong style={{ color: theme.text.secondary, fontSize: 11 }}>Resolution:</strong>
        {currentBadge ?? (
          <span style={{ color: theme.text.tertiary, fontSize: 11, fontStyle: 'italic' }}>
            pending
          </span>
        )}
        {!editing && (
          <button
            onClick={() => {
              setEditing(true);
              setResolution(change.resolution ?? 'implemented');
              setNote(change.resolutionNote ?? '');
              setError(null);
            }}
            style={{
              marginLeft: 'auto',
              padding: '2px 8px',
              background: 'transparent',
              color: theme.text.secondary,
              border: `1px solid ${theme.surface.borderStrong}`,
              borderRadius: theme.radius.sm,
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            {change.resolution ? 'Edit' : 'Mark resolution…'}
          </button>
        )}
      </div>
      {editing && (
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <select
            value={resolution}
            onChange={(e) => setResolution(e.target.value as AppChangeResolution)}
            disabled={submitting}
            style={{
              padding: '4px 6px',
              background: theme.surface.header,
              color: theme.text.primary,
              border: `1px solid ${theme.surface.borderStrong}`,
              borderRadius: theme.radius.sm,
              fontSize: 12,
            }}
          >
            {RESOLUTION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={submitting}
            rows={2}
            placeholder={
              noteRequired
                ? 'REQUIRED: explain the resolution reason'
                : 'Optional: explain the resolution reason'
            }
            style={{
              padding: 6,
              background: theme.surface.header,
              color: theme.text.primary,
              border: `1px solid ${noteRequired && !trimmedNote ? theme.status.errorBorder : theme.surface.borderStrong}`,
              borderRadius: theme.radius.sm,
              fontSize: 12,
              fontFamily: theme.fonts.sans,
              resize: 'vertical',
            }}
          />
          {error && <div style={{ color: theme.status.error, fontSize: 11 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => void onSubmit()}
              disabled={submitDisabled}
              style={{
                padding: '4px 10px',
                background: submitDisabled ? theme.surface.border : theme.brand.primary,
                color: submitDisabled ? theme.text.tertiary : theme.text.onBrand,
                border: 'none',
                borderRadius: theme.radius.sm,
                fontSize: 12,
                cursor: submitDisabled ? 'not-allowed' : 'pointer',
                fontWeight: 600,
              }}
            >
              {submitting ? 'Saving…' : 'Save resolution'}
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setError(null);
              }}
              disabled={submitting}
              style={{
                padding: '4px 10px',
                background: 'transparent',
                color: theme.text.secondary,
                border: `1px solid ${theme.surface.borderStrong}`,
                borderRadius: theme.radius.sm,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export interface ResolutionBadgeProps {
  resolution: AppChangeResolution;
  note?: string;
  resolvedByDisplayName?: string;
  resolvedBy?: string;
  resolvedAt?: string;
}

export function ResolutionBadge(props: ResolutionBadgeProps): JSX.Element {
  const palette = RESOLUTION_PALETTE[props.resolution];
  const attribution = props.resolvedByDisplayName || props.resolvedBy;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
      }}
    >
      <span
        style={{
          background: palette.bg,
          color: palette.fg,
          padding: '1px 6px',
          borderRadius: theme.radius.sm,
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}
      >
        {palette.label}
      </span>
      {attribution && (
        <span style={{ color: theme.text.tertiary, fontSize: 11 }}>
          {attribution}
          {props.resolvedAt ? ` · ${props.resolvedAt}` : ''}
        </span>
      )}
      {props.note && (
        <span
          style={{
            color: theme.text.secondary,
            fontSize: 11,
            fontStyle: 'italic',
            display: 'block',
            width: '100%',
            marginTop: 2,
          }}
        >
          {props.note}
        </span>
      )}
    </span>
  );
}
